import { Alert, Badge, Button, Card, Group, Stack, Text } from "@mantine/core";
import {
  IconChevronRight,
  IconEdit,
  IconInfoCircle,
  IconPlus,
} from "@tabler/icons-react";
import { client } from "@/api-client";
import type { ScheduleOutput, TimeRangeOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import {
  convertTimeToString,
  daysOptions,
} from "@/pages/trainer/profile/common";
import { TrainerRoutes, useNavigator } from "@/routers/routes";

export const Schedules = () => {
  const { navigate } = useNavigator();

  const { data: schedules } = client.useQuery(
    "get",
    "/trainer/profile/schedules",
    undefined,
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  const { data: sessionTypes } = client.useQuery(
    "get",
    "/trainer/profile/session-types",
    undefined,
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  if (!schedules || !sessionTypes) {
    return <FancyLoader />;
  }

  const handleCreateSchedule = () => {
    navigate({
      path: TrainerRoutes.PROFILE.SCHEDULE_EDIT,
      params: { scheduleId: "new" },
    });
  };

  const handleScheduleClick = (schedule: ScheduleOutput) => {
    navigate({
      path: TrainerRoutes.PROFILE.SCHEDULE_EDIT,
      params: { scheduleId: schedule.id },
    });
  };

  const handleEditSchedule = (
    schedule: ScheduleOutput,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation(); // Prevent triggering the card click
    navigate({
      path: TrainerRoutes.PROFILE.SCHEDULE_EDIT,
      params: { scheduleId: schedule.id },
    });
  };

  const getSessionTypesForSchedule = (scheduleId: string) => {
    return sessionTypes.filter((st) => st.scheduleId === scheduleId);
  };

  const formatWorkdays = (dayTimeRanges: Record<string, TimeRangeOutput>) => {
    return Object.keys(dayTimeRanges)
      .map((day) =>
        daysOptions.find((opt) => opt.value === day)?.label.slice(0, 3),
      )
      .join(", ");
  };

  const formatWorkingHours = (
    dayTimeRanges: Record<string, TimeRangeOutput>,
  ) => {
    const timeRanges = Object.values(dayTimeRanges);
    if (timeRanges.length === 0) return "No hours set";

    // Check if all days have the same time ranges
    const first = timeRanges[0];
    if (!first) return "No hours set";

    const allSame = timeRanges.every(
      (range) =>
        range.startTime === first.startTime && range.endTime === first.endTime,
    );

    if (allSame) {
      // Show uniform time range
      return `${convertTimeToString(first.startTime)} - ${convertTimeToString(first.endTime)}`;
    } else {
      // Show "Varies by day"
      return "Varies by day";
    }
  };

  const renderSchedulesList = () => {
    if (schedules.length === 0) {
      return (
        <Card withBorder>
          <Stack align="center" gap="md" py="xl">
            <Text size="lg" fw={500} c="dimmed">
              No schedules created yet
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Create your first schedule to define when you're available for
              sessions
            </Text>
          </Stack>
        </Card>
      );
    }

    return (
      <Stack gap="md">
        {schedules.map((schedule) => {
          const associatedSessionTypes = getSessionTypesForSchedule(
            schedule.id,
          );

          return (
            <Card
              key={schedule.id}
              withBorder
              style={{ cursor: "pointer" }}
              onClick={() => handleScheduleClick(schedule)}
            >
              <Stack gap="sm">
                <Group justify="space-between" align="flex-start">
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Group align="center" gap="sm">
                      <Text size="lg" fw={500}>
                        {schedule.name}
                      </Text>
                      {associatedSessionTypes.length > 0 && (
                        <Badge variant="light" size="sm">
                          {associatedSessionTypes.length} session type
                          {associatedSessionTypes.length === 1 ? "" : "s"}
                        </Badge>
                      )}
                    </Group>

                    <Group gap="lg">
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">
                          Days:
                        </Text>
                        <Text size="sm">
                          {formatWorkdays(schedule.dayTimeRanges)}
                        </Text>
                      </Group>
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">
                          Hours:
                        </Text>
                        <Text size="sm">
                          {formatWorkingHours(schedule.dayTimeRanges)}
                        </Text>
                      </Group>
                    </Group>

                    <Group gap="lg">
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">
                          Booking window:
                        </Text>
                        <Text size="sm">
                          {schedule.minDaysAhead}-{schedule.maxDaysAhead} days
                        </Text>
                      </Group>
                    </Group>

                    {associatedSessionTypes.length > 0 && (
                      <Group gap="xs">
                        <Text size="sm" c="dimmed">
                          Used by:
                        </Text>
                        <Text size="sm">
                          {associatedSessionTypes
                            .map((st) => st.name)
                            .join(", ")}
                        </Text>
                      </Group>
                    )}
                  </Stack>

                  <Group gap="xs">
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      leftSection={<IconEdit size={14} />}
                      onClick={(event) => handleEditSchedule(schedule, event)}
                    >
                      Edit
                    </Button>
                    <IconChevronRight
                      size={16}
                      color="var(--mantine-color-dimmed)"
                    />
                  </Group>
                </Group>
              </Stack>
            </Card>
          );
        })}
      </Stack>
    );
  };

  return (
    <Stack gap="md">
      <Header
        title="Schedules"
        actionControl={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateSchedule}
            variant="outline"
          >
            Add Schedule
          </Button>
        }
      />

      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        Schedules define when you're available for different types of sessions.
        Each session type can be assigned to a specific schedule with its own
        working hours and booking rules.
      </Alert>

      {renderSchedulesList()}
    </Stack>
  );
};
